import angularModule from 'Careers/angularModule/scripts/careers_module';
import trashcanSlateGrey from 'vectors/trashcan_slate_grey.svg';

angularModule.factory('EducationExperienceDetailHelper', [
    '$injector',
    $injector => {
        const TranslationHelper = $injector.get('TranslationHelper');
        const EducationExperience = $injector.get('EducationExperience');

        let degreeOptionsForNgModel;
        let lastNgModelRef;
        const translationHelper = new TranslationHelper('careers.edit_career_profile.education_experience_detail');
        const yearOptions = [];
        for (let j = new Date().getUTCFullYear() + 5; j >= 1940; j--) {
            yearOptions.push(j);
        }

        const levelOptions = EducationExperience.levelOrder.map(level => ({
            value: level,
            label: translationHelper.get(`degree_level_${level}`),
        }));

        function buildOption(translationKey) {
            const fullStringKey = `${translationKey.replace(/_[0-9]$/, '')}_full`;
            const fullString = translationHelper.get(fullStringKey);
            const shortString = translationHelper.get(translationKey);
            return {
                sort: fullString,
                value: translationKey,
                label: shortString === fullString ? shortString : `${shortString} (${fullString})`,
                // allows searching for shortened string without needing the periods
                search: shortString.split('.').join(''),
            };
        }

        function rebuildDegreeOptionsForNgModel(ngModel) {
            const options = EducationExperience.degreeOptions
                .map(buildOption)
                .sort((a, b) => a.label.localeCompare(b.label, translationHelper.getCurrentLanguage()));

            // Surface any user-provided write-ins so selectize can render them as existing options.
            const additionalOptions = [
                ...new Set((ngModel || []).map(item => item.degree).filter(degree => !options.includes(degree))),
            ];
            additionalOptions.forEach((option, i) => {
                if (!option) return;
                options.push({
                    sort: options.length + i,
                    value: option,
                    label: option,
                    search: option.split('.').join(''),
                });
            });

            return options;
        }

        function getDegreeOptionsForNgModel(ngModel) {
            if (!degreeOptionsForNgModel || lastNgModelRef !== ngModel) {
                degreeOptionsForNgModel = rebuildDegreeOptionsForNgModel(ngModel);
                lastNgModelRef = ngModel;
            }
            return degreeOptionsForNgModel;
        }

        // Filtered degree lists are memoized per experience so the reference stays stable
        // across digest cycles — otherwise selectize's watcher sees a fresh array on every
        // iteration and triggers $rootScope:infdig.
        const filteredOptionsByExperience = new WeakMap();

        function getFilteredDegreeOptions(ngModel, educationExperience) {
            const allOptions = getDegreeOptionsForNgModel(ngModel);
            if (!educationExperience) return allOptions;

            const level = educationExperience.degree_level;
            const currentDegree = educationExperience.degree;
            if (!level) return allOptions;

            const signature = `${level}|${currentDegree ?? ''}|${allOptions.length}`;
            const cached = filteredOptionsByExperience.get(educationExperience);
            if (cached && cached.signature === signature) {
                return cached.options;
            }

            const filtered = allOptions.filter(
                option =>
                    EducationExperience.levelForDegreeKey(option.value) === level || option.value === currentDegree,
            );
            filteredOptionsByExperience.set(educationExperience, { signature, options: filtered });
            return filtered;
        }

        return {
            trashcanSlateGrey,
            graduateOptions: [
                {
                    value: true,
                    stringValue: 'true',
                    label: translationHelper.get('yes'),
                },
                {
                    value: false,
                    stringValue: 'false',
                    label: translationHelper.get('no'),
                },
            ],
            // NOTE: this seems incorrect, but is not. These options are used
            // for educationExperience.willNotComplete. Selecting "yes" means
            // the program is or will be complete, hence the false value.
            // The opposite is true when selecting "no".
            completeOptions: [
                {
                    value: false,
                    stringValue: 'true',
                    label: translationHelper.get('yes'),
                },
                {
                    value: true,
                    stringValue: 'false',
                    label: translationHelper.get('no'),
                },
            ],
            yearOptions,
            levelOptions,
            degreeOptions: (ngModel, educationExperience) => getFilteredDegreeOptions(ngModel, educationExperience),
            resetGraduateState: (educationExperience, resetYears = false) => {
                // In the past we had a question that set will_not_complete for both degree
                // and non-degree experiences. However, for degree experiences we broke that
                // question into two pieces that now set has_graduated and / or will_graduate.
                if (educationExperience.has_graduated || educationExperience.will_graduate) {
                    educationExperience.will_not_complete = false;
                } else if (educationExperience.has_graduated === false && educationExperience.will_graduate === false) {
                    educationExperience.will_not_complete = true;
                }

                // Undo the will_graduate value if a user decides to answer yes to has_graduated,
                // since both being true doesn't make sense. Be sure to use null instead of
                // undefined so the server will get the value, in case the user had already saved.
                // We have a server validation that ensures both can't be true.
                if (educationExperience.has_graduated) {
                    educationExperience.will_graduate = null;
                }

                if (resetYears) {
                    // It felt like a better UX experience to reset the years when changing the context of the question
                    educationExperience.start_year = null;
                    educationExperience.graduation_year = null;
                }
            },
        };
    },
]);
